import {
  formatearDia,
  formatearFechaHora,
  formatearHora,
  formatearRevision,
  NOMBRE_ESTADO,
  NOMBRE_ESTADO_HOJA,
  type ResultadoDeHoja,
} from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { ResponsablesDeFormato } from "../componentes/ResponsablesDeFormato.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  EstadoVacio,
  PildoraDeHoja,
  Tarjeta,
} from "../componentes/ui.tsx";
import { useAbrirHoja } from "../componentes/useAbrirHoja.ts";

/**
 * Fase 3b · las hojas llenas de un formato (GES-REG-022): la lista por fecha con los filtros del
 * auditor, quién lo llena, y los botones para abrir la hoja de hoy. La hoja en sí es Hoja.tsx.
 */

/** Los filtros de `hojas.listar`: lo que dijo quien la llenó, por verificar, o sin decir. */
type FiltroDeResultado = ResultadoDeHoja | "por_verificar" | "sin_indicar";

export function PaginaHojas() {
  const { codigo } = useParams({ from: "/app/documentos/$codigo/hojas" });
  const trpc = useTRPC();
  // Filtros del auditor: el mes y lo que dijo quien la llenó al terminar.
  const [mes, setMes] = useState("");
  const [resultado, setResultado] = useState<FiltroDeResultado | "">("");
  const lista = useQuery({
    ...trpc.hojas.listar.queryOptions({
      codigo,
      ...(mes ? { mes } : {}),
      ...(resultado ? { resultado } : {}),
    }),
    placeholderData: (previa) => previa,
  });
  const [aviso, setAviso] = useState<string | null>(null);
  const abrir = useAbrirHoja(setAviso);
  if (lista.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (lista.isError) return <EstadoVacio>{lista.error.message}</EstadoVacio>;
  const d = lista.data;
  return (
    <div className="max-w-[980px]">
      <Encabezado
        overline={`Documentos › ${d.documento.codigo} › Hojas`}
        titulo={d.documento.nombre}
        acciones={
          <>
            <BotonEnlace to="/documentos/$codigo" params={{ codigo }}>
              Volver al documento
            </BotonEnlace>
            {d.puedeLlenar && d.formulario && (
              <Boton onClick={() => abrir.mutate({ codigo })} disabled={abrir.isPending}>
                {abrir.isPending ? "Abriendo…" : "Hoja de hoy"}
              </Boton>
            )}
            {d.puedeLlenar && d.formulario && d.misDeHoy > 0 && (
              <Boton
                variante="secundario"
                onClick={() => abrir.mutate({ codigo, otra: true })}
                disabled={abrir.isPending}
              >
                + Otra hoja
              </Boton>
            )}
          </>
        }
      />
      {aviso && <Aviso className="mb-4">{aviso}</Aviso>}
      {!d.formulario && (
        <Aviso tono="ambar" className="mb-4">
          {d.convirtiendo
            ? "El archivo del formato se está convirtiendo a PDF. En un momento se podrá llenar."
            : "Este formato no tiene contenido para llenar."}
        </Aviso>
      )}
      {d.formulario && d.formulario.estado !== "vigente" && (
        <Aviso tono="ambar" className="mb-4">
          El formato está en {NOMBRE_ESTADO[d.formulario.estado].toLowerCase()}: las hojas se llenan
          sobre la Rev. {formatearRevision(d.formulario.numero)} mientras se aprueba.
        </Aviso>
      )}
      <ResponsablesDeFormato codigo={codigo} />
      <Tarjeta
        titulo="Hojas llenas"
        ayuda={`Cada hoja guarda quién la llenó, cuándo, y lo que escribió encima del formato.${
          d.retencionMeses ? ` Se conserva ${d.retencionMeses} meses desde su día.` : ""
        }`}
      >
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="text-[12px] font-semibold text-texto-2">
            Mes
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              className="mt-1 block rounded-md border border-borde px-2.5 py-1.5 text-[13px] font-normal"
            />
          </label>
          <label className="text-[12px] font-semibold text-texto-2">
            Cómo quedó
            <select
              value={resultado}
              onChange={(e) => setResultado(e.target.value as FiltroDeResultado | "")}
              className="mt-1 block rounded-md border border-borde px-2.5 py-1.5 text-[13px] font-normal"
            >
              <option value="">Todas</option>
              <option value="no_aceptable">Con no aceptables</option>
              <option value="por_verificar">{NOMBRE_ESTADO_HOJA.por_verificar}</option>
              <option value="aceptable">Todo aceptable</option>
              <option value="sin_indicar">Sin indicar</option>
            </select>
          </label>
          {(mes || resultado) && (
            <button
              type="button"
              onClick={() => {
                setMes("");
                setResultado("");
              }}
              className="pb-2 text-[12.5px] font-semibold text-marino"
            >
              Quitar filtros
            </button>
          )}
        </div>
        {d.hojas.length === 0 ? (
          <EstadoVacio>
            {mes || resultado
              ? "Ninguna hoja cumple el filtro."
              : 'Todavía no hay hojas. La primera se abre con "Hoja de hoy".'}
          </EstadoVacio>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-semibold tracking-[.08em] text-texto-3 uppercase">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Llenó</th>
                  <th className="px-3 py-2">Cómo quedó</th>
                  <th className="px-3 py-2">Trazos</th>
                  <th className="px-3 py-2">Guardada</th>
                  <th className="px-3 py-2">Se conserva hasta</th>
                </tr>
              </thead>
              <tbody>
                {d.hojas.map((h) => (
                  <tr key={h.id} className="border-t border-borde/60 hover:bg-fondo">
                    <td className="px-3 py-2 font-mono">
                      <Link
                        to="/hojas/$id"
                        params={{ id: h.id }}
                        className="font-semibold text-marino"
                      >
                        {formatearDia(h.fecha)}
                      </Link>
                      <span className="ml-2 text-[12px] text-texto-3">
                        {formatearHora(h.creadaEn)}
                      </span>
                    </td>
                    <td className="px-3 py-2">{h.quienNombre}</td>
                    <td className="px-3 py-2">
                      <PildoraDeHoja estado={h.estado} resultado={h.resultado} />
                    </td>
                    <td className="px-3 py-2 font-mono">{h.trazos || "—"}</td>
                    <td className="px-3 py-2 text-texto-2">
                      {formatearFechaHora(h.actualizadaEn)}
                    </td>
                    <td className="px-3 py-2 text-texto-2">
                      {h.conservarHasta ? formatearDia(h.conservarHasta) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </div>
  );
}
